/**
 * BackendRegistry — maps a {@link ScriptType} to its {@link IScriptExecutorFactory}.
 *
 * The single dispatch point for the unified `ScriptSubsystem`: it owns no executor type
 * directly, so any backend (TypeScript, the frozen Redprint graph interpreter, a future
 * optional C# backend) plugs in by registering a factory. Replaces the hardcoded
 * `new GraphExecutor(...)` / `new Function(...)` wiring in the legacy subsystems.
 */

import type { IScriptExecutor, IScriptExecutorFactory, ScriptType } from "./IScriptExecutor";

export class BackendRegistry {
  private readonly _factories = new Map<ScriptType, IScriptExecutorFactory>();

  /** Register a backend factory (replaces any existing one for its scriptType). */
  register(factory: IScriptExecutorFactory): void {
    this._factories.set(factory.scriptType, factory);
  }

  unregister(scriptType: ScriptType): boolean {
    return this._factories.delete(scriptType);
  }

  has(scriptType: ScriptType): boolean {
    return this._factories.has(scriptType);
  }

  get(scriptType: ScriptType): IScriptExecutorFactory | undefined {
    return this._factories.get(scriptType);
  }

  /** Create a fresh executor instance for `assetId` via the registered backend. */
  create(scriptType: ScriptType, assetId: string): IScriptExecutor {
    const factory = this._factories.get(scriptType);
    if (!factory) {
      throw new Error(`[BackendRegistry] No script backend registered for '${scriptType}'`);
    }
    return factory.create(assetId);
  }

  /** All registered script types. */
  types(): ScriptType[] {
    return [...this._factories.keys()];
  }

  clear(): void {
    this._factories.clear();
  }
}
